"use client"

import { createContext, useContext, type ReactNode } from "react"

/**
 * Holds the DOM element the active Requests-group tab portals its
 * <ViewSelector /> into, so the dropdown lands in the workflow-hub's
 * top-right header area (next to the search icon and kebab menu) — the
 * same slot WorkOrder's ViewSelector occupies — while staying mounted
 * inside its own per-tab WorkflowProvider scope.
 *
 * Why portal instead of just rendering at hub level: each Requests-group
 * tab (ClientRequest / VMS Requests / VMS Emails) has its own nested
 * WorkflowProvider. A hub-level ViewSelector would read WorkOrder state
 * (the WorkflowProvider at hub level), not the active Requests-group
 * tab's state. Portal lets us keep the React tree position (under the
 * right WorkflowProvider, so useWorkflow() resolves correctly) while
 * placing the rendered output in the hub-level header.
 *
 * `null` when no slot is mounted — children should skip the portal in
 * that case (e.g. on the WorkOrder tab the slot ref isn't set because
 * the hub renders its own ViewSelector inline; legacy callers that
 * don't bother wrapping in a provider also see `null` and degrade
 * gracefully).
 */
const RequestsTabHeaderSlotContext = createContext<HTMLElement | null>(null)

/**
 * Opens the hub-level View Designer. The designer is mounted once at hub level (scoped to the active
 * tab's entity type there), so the portaled per-tab ViewSelectors can't own it — they trigger it
 * through this callback instead. `null` when the hub hasn't provided one, in which case the
 * ViewSelector hides its "View Designer" entry exactly as before.
 */
const RequestsTabOpenViewManagerContext = createContext<(() => void) | null>(null)

export function RequestsTabHeaderSlotProvider({
  slotElement,
  onOpenViewManager,
  children,
}: {
  slotElement: HTMLElement | null
  onOpenViewManager?: () => void
  children: ReactNode
}) {
  return (
    <RequestsTabHeaderSlotContext.Provider value={slotElement}>
      <RequestsTabOpenViewManagerContext.Provider value={onOpenViewManager ?? null}>
        {children}
      </RequestsTabOpenViewManagerContext.Provider>
    </RequestsTabHeaderSlotContext.Provider>
  )
}

export function useRequestsTabHeaderSlot(): HTMLElement | null {
  return useContext(RequestsTabHeaderSlotContext)
}

export function useRequestsTabOpenViewManager(): (() => void) | null {
  return useContext(RequestsTabOpenViewManagerContext)
}
